/* eslint-disable react/jsx-key */
import React from 'react';
import PT from 'prop-types';
import { injectIntl, FormattedMessage, defineMessages } from 'react-intl';
import { useDispatch } from 'react-redux';
import { ReactComponent as CancelIcon } from '@lifesize/ux-assets/vectors/svgIcons/cancel.svg';
import { ReactComponent as HandIcon } from '@lifesize/ux-assets/vectors/svgIcons/hand.svg';
import { ReactComponent as MicIcon } from '@lifesize/ux-assets/vectors/svgIcons/mic.svg';
import { ReactComponent as MicOffIcon } from '@lifesize/ux-assets/vectors/svgIcons/mic-off.svg';
import ContactCardHeader from 'components/Contacts/ContactCardHeader';
import Tooltip from 'components/Tooltip/Tooltip';
import InCallParticipant from 'components/InCall/InCallParticipantList/InCallParticipant';
import { participantFilterType } from 'constants/ParticipantList';
import classes from './RemoteModeratorPanel.module.scss';
import MeetingLock from 'components/InCall/MeetingLock';
import PanelList from './PanelList';
import SharingBlock from 'components/InCall/SharingBlock';
import WaitingRoomParticipant from 'components/InCall/InCallParticipantList/WaitingRoomParticipant';
import { ModeratorList } from 'components/InCall/InCallParticipantList/ModeratorList';
import { RemoteModeratorList } from 'components/InCall/InCallParticipantList/RemoteModeratorList';
import { LecturerList } from 'components/InCall/InCallParticipantList/LecturerList';
import { openInCallModal } from 'actions/InCallModal';
import Modals from 'constants/Modals';
import { moderatorActions } from '@lifesize/nucleus';
import { setPillNotificationWithTimeout } from 'actions/Notification';
import Logger from 'utils/logger';
import { hooks } from '@lifesize/clients.sdk';
import useFeatureFlag from 'hooks/useFeatureFlag';
import { FLAG_RAISE_HAND } from 'constants/FeatureFlags';
import { isEscalated, isVMR } from 'utils/conference';

const messages = defineMessages({
  muteAll: {
    id: 'components.incall.footer.notification.muteall',
    defaultMessage: 'All participants are now muted. They cannot unmute themselves.'
  },
  unMuteAll: {
    id: 'components.incall.footer.notification.unMuteAll',
    defaultMessage: 'All participants are now unmuted, unless they have muted themselves.'
  },
  filterAll: {
    id: 'components.inCall.participantList.filter.all',
    defaultMessage: 'Participants ({participants, number})'
  },
  waitingRoomParticipantCount: {
    id: 'components.inCall.waitingRoom.header',
    description: 'In call waiting Room header',
    defaultMessage: 'Waiting Room ({participants, number})'
  },
  participantsCount: {
    id: 'components.inCall.participantList.header',
    description: 'In call participant list header',
    defaultMessage: 'Participants ({participants, number})'
  },
  meetingSettings: {
    id: 'components.remoteModeratorPanel.meetingSettings',
    description: 'Meeting Settings List title',
    defaultMessage: 'Meeting Settings'
  }
});
export const RemoteModerationPanel = (props) => {
  const dispatch = useDispatch();
  const { intl } = props;
  const conference = hooks.useConferenceInfo();
  const participantsState = hooks.useParticipants();
  const moderators = hooks.useModerators();
  const participantsList = hooks.useParticipants();
  const remoteModerators = hooks.useRemoteModerators();
  const waitingRoomUsers = hooks.useWaitingRoomUsers().filter((participant) => !!participant.name); // ignore users that are processing (no name)
  const lecturers = participantsState.filter((p) => p?.uuid === conference?.lecturerUuid); // should be one or none, but returning an array for consistency
  const waitingRoomUsersCount = waitingRoomUsers?.length || 0;
  const isWaitingRoomEnabled = hooks.useIsWaitingRoomEnabled();
  const canMuteAll = isEscalated(conference?.state);
  const canEndMeetingForAll = isVMR(conference.type) || isEscalated(conference?.state);
  const isAudioMutedByServer = conference?.serverMuteState;
  const raisedHands = hooks.useParticipantsWithRaisedHands();
  const raisedHandsCount = raisedHands?.length || 0;
  const unmuted = participantsList.filter((p) => !(p.localAudioMuteState || p.serverAudioMuteState));
  const showLowerAllHandsButton = useFeatureFlag(FLAG_RAISE_HAND);
  const { participantFilter } = props;

  const getParticipants = () => {
    switch (participantFilter) {
      case participantFilterType.raisedHands:
        return raisedHands;
      case participantFilterType.unmuted:
        return unmuted;
      default:
        return participantsList;
    }
  };
  const handleConfirmRemoteModerationEnd = () => dispatch(openInCallModal(Modals.endRemoteModeration));
  const onEndMeetingForAll = () => dispatch(openInCallModal(Modals.endCallForAll, { isRemoteModerator: true }));
  const onLowerAllHandsClicked = () => dispatch(moderatorActions.lowerAllRaisedHands());
  const onLowerHandClicked = (participantId) => dispatch(moderatorActions.lowerParticipantHand(participantId));
  const onMuteAllClicked = async (mute) => {
    try {
      await dispatch(moderatorActions.setAllParticipantsMuteValue(mute));
      dispatch(setPillNotificationWithTimeout(intl.formatMessage(mute ? messages.muteAll : messages.unMuteAll)));
    } catch (e) {
      Logger.info(`onMuteAllClicked failure (${mute})`, e);
    }
  };
  const onMuteParticipantClicked = (participant) => {
    dispatch(moderatorActions.setParticipantMuteValue(participant.participantId, !participant.serverAudioMuteState));
  };
  const onSelectRaisedHandClicked = (participantId) => {
    // TODO: stretch goal is to use this to select the participant (give them the floor); for now, just lower hand
    dispatch(moderatorActions.lowerParticipantHand(participantId));
  };
  const onStopPresenterClicked = (participant) =>
    dispatch(openInCallModal(Modals.stopPresenter, { participant, isRemoteModerator: true }));
  const removeFromMeetingClicked = (participant) =>
    dispatch(openInCallModal(Modals.removeFromMeeting, { participant, isRemoteModerator: true }));
  const handleFilterNone = () => {
    props.selectParticipantFilter(participantFilterType.none);
  };

  const handleFilterHandsRaised = () => {
    props.selectParticipantFilter(participantFilterType.raisedHands);
  };

  const handleFilterUnmuted = () => {
    props.selectParticipantFilter(participantFilterType.unmuted);
  };

  const preventClickThrough = (evt) => {
    evt.stopPropagation();
  };
  const getEmptyListMessage = () => {
    switch (participantFilter) {
      case participantFilterType.raisedHands:
        return (
          <FormattedMessage
            id="components.inCall.participantList.noRaisedHands"
            defaultMessage={'There are no hands raised'}
          />
        );
      case participantFilterType.unmuted:
        return (
          <FormattedMessage
            id="components.inCall.participantList.noUnmuted"
            defaultMessage={'There are no unmuted participants'}
          />
        );
      default:
        return '';
    }
  };
  return (
    <div className={classes.background} onClick={handleConfirmRemoteModerationEnd}>
      <div className={classes.container} onClick={preventClickThrough}>
        <ContactCardHeader
          contact={props.contact}
          handleRemoteModerate={props.handleRemoteModerate}
          remoteModerationIsActiveForThisMeeting={true}
          scroll={0}
          userCanRemoteModerate={true}
        />
        <section className={classes.panelContent}>
          {isWaitingRoomEnabled && (
            <PanelList
              dataTestId={'remote-moderator-waiting-room-list'}
              showExpandIcon={!!waitingRoomUsersCount}
              header={intl.formatMessage(messages.waitingRoomParticipantCount, {
                participants: waitingRoomUsersCount || 0
              })}
            >
              {waitingRoomUsers.map(
                (participant, index) =>
                  participant?.name &&
                  participant?.participantId && (
                    <WaitingRoomParticipant
                      key={`participant-${participant.participantId}-${index}`}
                      participant={participant}
                    />
                  )
              )}
            </PanelList>
          )}
          <PanelList
            dataTestId={'participants-list'}
            showExpandIcon={true}
            header={intl.formatMessage(messages.participantsCount, { participants: participantsList.length || 0 })}
            fillClass={'fillRemaining'}
          >
            {showLowerAllHandsButton && (
              <div className={classes.filterContainer}>
                <button
                  data-testid="allParticipants"
                  disabled={participantFilter === participantFilterType.none}
                  onClick={handleFilterNone}
                >
                  <span>
                    <FormattedMessage
                      id="components.inCall.participantList.filter.all"
                      defaultMessage="All {total}"
                      values={{ total: participantsList.length || 0 }}
                    />
                  </span>
                </button>
                <button
                  data-testid="handRaisedParticipants"
                  disabled={participantFilter === participantFilterType.raisedHands}
                  onClick={handleFilterHandsRaised}
                >
                  <HandIcon className={!!raisedHandsCount ? classes.raisedHands : ''} />
                  <span>{raisedHands?.length || 0}</span>
                </button>
                <button
                  data-testid="unmutedParticipants"
                  disabled={participantFilter === participantFilterType.unmuted}
                  onClick={handleFilterUnmuted}
                >
                  <MicIcon data-testid="micIcon" /> <span>{unmuted.length || 0}</span>
                </button>
              </div>
            )}
            <div className={classes.participantList}>
              {getParticipants().map((participant) => (
                <InCallParticipant
                  canBeMuted={canMuteAll}
                  key={`participant-${participant.participantId}`}
                  onLowerHandClicked={onLowerHandClicked}
                  onMuteParticipantClicked={onMuteParticipantClicked}
                  onRemoveFromMeetingClicked={removeFromMeetingClicked}
                  onSelectRaisedHandClicked={onSelectRaisedHandClicked}
                  onStopPresenterClicked={onStopPresenterClicked}
                  userIsModerator={true}
                  participant={participant}
                />
              ))}
              {getParticipants()?.length === 0 && <p className={classes.emptyListMessage}>{getEmptyListMessage()}</p>}
              {participantFilter === participantFilterType.none && (
                <div className={classes.participantRoles}>
                  {moderators && moderators.length > 0 && <ModeratorList filteredModerators={moderators} />}
                  {remoteModerators.length > 0 && <RemoteModeratorList filteredRemoteModerators={remoteModerators} />}
                  {lecturers?.length > 0 && <LecturerList filteredLecturers={lecturers} />}
                </div>
              )}
            </div>
          </PanelList>
          <PanelList
            dataTestId={'meeting-settings'}
            showExpandIcon={true}
            header={intl.formatMessage(messages.meetingSettings)}
            collapseList
            noScroll
          >
            <SharingBlock outOfCall />
            <MeetingLock outOfCall />
          </PanelList>
        </section>
        <div className={classes.buttonContainer}>
          {canMuteAll && !isAudioMutedByServer && (
            <button
              data-testid="btnMuteAll"
              key="muteAll"
              onClick={() => {
                onMuteAllClicked(true);
              }}
            >
              <div className={classes.tooltipWrapper}>
                <Tooltip
                  location={'top'}
                  overlapCarat={false}
                  label={
                    <FormattedMessage
                      id="contacts.tooltip.muteAll"
                      defaultMessage="Participants muted by the moderator cannot unmute themselves."
                    />
                  }
                />
              </div>
              <MicOffIcon data-testid="micOffIcon" className={classes.buttonIcon} />
              <FormattedMessage id="components.inCall.mute" defaultMessage="Mute All" />
            </button>
          )}
          {canMuteAll && isAudioMutedByServer && (
            <button
              data-testid="btnUnmuteAll"
              key="unmuteAll"
              onClick={() => {
                onMuteAllClicked(false);
              }}
            >
              <div className={classes.tooltipWrapper}>
                <Tooltip
                  location={'top'}
                  overlapCarat={false}
                  label={
                    <FormattedMessage
                      id="contacts.tooltip.unmuteAll"
                      defaultMessage="Unmute participants. Self-muted participants will remain muted."
                    />
                  }
                />
              </div>
              <MicIcon data-testid="micIcon" className={classes.buttonIcon} />
              <FormattedMessage id="components.inCall.unMute" defaultMessage="Unmute All" />
            </button>
          )}
          {showLowerAllHandsButton && (
            <button data-testid="btnLowerAllHands" disabled={!raisedHandsCount} onClick={onLowerAllHandsClicked}>
              <HandIcon data-testid="hand-icon" className={classes.buttonIcon} />
              <FormattedMessage id="components.inCall.lowerAllHands" defaultMessage="Lower All Hands" />
            </button>
          )}
          {canEndMeetingForAll && (
            <button data-testid="btnStopRemoteModeration" onClick={onEndMeetingForAll}>
              <CancelIcon data-testid="cancelIcon" className={classes.buttonIcon} />
              <FormattedMessage id="components.inCall.endMeetingForAll" defaultMessage="End Meeting for All" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

RemoteModerationPanel.propTypes = {
  canChangeMeetingLock: PT.bool,
  canEndMeetingForAll: PT.bool,
  canMuteAll: PT.bool,
  contact: PT.object.isRequired, // ownProps
  handleConfirmRemoteModerationEnd: PT.func.isRequired,
  handleRemoteModerate: PT.func, // ownProps
  isAudioMutedByServer: PT.bool,
  moderators: PT.array,
  onEndMeetingForAll: PT.func.isRequired,
  onLockMeetingClicked: PT.func.isRequired,
  onLowerAllHandsClicked: PT.func.isRequired,
  onLowerHandClicked: PT.func.isRequired,
  onMuteAllClicked: PT.func.isRequired,
  onMuteParticipantClicked: PT.func.isRequired,
  onSelectRaisedHandClicked: PT.func.isRequired,
  onStopPresenterClicked: PT.func.isRequired,
  participantFilter: PT.string, // ownProps
  participants: PT.array,
  participantsCount: PT.number,
  remoteModerators: PT.array.isRequired,
  removeFromMeetingClicked: PT.func,
  selectParticipantFilter: PT.func.isRequired, // ownProps
  showLowerAllHandsButton: PT.bool,
  unmutedCount: PT.number,
  isWaitingRoomEnabled: PT.bool
};

export default injectIntl(RemoteModerationPanel);
